import React, { lazy } from 'react';

import PricingDetail from '../pages/PricingDetail';
import PricingMatrix from '../pages/PricingMatrix';
import SLACredits from '../pages/SLACredits';
import UserManagement from '../pages/features/admin/UserManagement';
import PermissionsMatrix from '../pages/features/admin/PermissionsMatrix';
import AuditTrail from '../pages/features/admin/AuditTrail';
import ApprovalWorkflows from '../pages/features/admin/ApprovalWorkflows';
import Workbook from '../pages/Workbook';
import WorkbookFeature from '../pages/WorkbookFeature';
import DsatTargets from '../pages/DsatTargets';
import CreditScorecard from '../pages/CreditScorecard';
import SubsidyCalculator from '../pages/SubsidyCalculator';
import PredictiveAlertsDashboard from '../pages/PredictiveAlertsDashboard';
import DemandHeatmap from '../pages/DemandHeatmap';
import RoleBasedDashboard from '../components/RoleBasedDashboard';

const Governance = lazy(() => import('../pages/Governance'));
const NetworkOps = lazy(() => import('../pages/NetworkOps'));
const Revenue = lazy(() => import('../pages/Revenue'));
const Programs = lazy(() => import('../pages/Programs'));
const RoamingHub = lazy(() => import('../pages/RoamingHub'));
const Compliance = lazy(() => import('../pages/Compliance'));
const IncidentCenter = lazy(() => import('../pages/IncidentCenter'));
const ESGDashboard = lazy(() => import('../pages/ESGDashboard'));
const PilotAnalytics = lazy(() => import('../pages/PilotAnalytics'));
const ExportsCenter = lazy(() => import('../pages/ExportsCenter'));
const Admin = lazy(() => import('../pages/features/admin/Admin'));
const Lifecycle = lazy(() => import('../pages/Lifecycle'));
const GovPlanner = lazy(() => import('../pages/features/government/GovPlanner'));
const DISCOMBoard = lazy(() => import('../pages/features/government/DISCOMBoard'));
const StatusSnapshot = lazy(() => import('../pages/features/government/StatusSnapshot'));
const SubsidyVerifier = lazy(() => import('../pages/features/government/SubsidyVerifier'));
const OpenDataFeeds = lazy(() => import('../pages/features/government/OpenDataFeeds'));
const PoliciesDocs = lazy(() => import('../pages/features/government/PoliciesDocs'));
const ReliabilityConsole = lazy(() => import('../pages/ReliabilityConsole'));
const FleetAPIs = lazy(() => import('../pages/FleetAPIs'));
const HostPortal = lazy(() => import('../pages/HostPortal'));
const EInvoicing = lazy(() => import('../pages/EInvoicing'));
const OperationsDashboard = lazy(() => import('../pages/OperationsDashboard'));
const FinanceDashboard = lazy(() => import('../pages/FinanceDashboard'));
const AdvancedAnalytics = lazy(() => import('../pages/AdvancedAnalytics'));
const FleetDashboard = lazy(() => import('../pages/FleetDashboard'));
const NetworkHealthMap = lazy(() => import('../pages/NetworkHealthMap'));
const ComponentShowcase = lazy(() => import('../pages/ComponentShowcase'));
const AdvancedDashboardExample = lazy(() => import('../pages/examples/AdvancedDashboard'));
const DataTableDemo = lazy(() => import('../pages/examples/DataTableDemo'));
const KanbanDemo = lazy(() => import('../pages/examples/KanbanDemo'));
const StationsManagement = lazy(() => import('../pages/enhanced/StationsManagement'));
const IncidentsKanban = lazy(() => import('../pages/enhanced/IncidentsKanban'));
const AIFeatures = lazy(() => import('../pages/AIFeatures'));
const PartnershipDashboard = lazy(() => import('../pages/partnerships/PartnershipDashboard'));
const OEMPartnersList = lazy(() => import('../pages/partnerships/OEMPartnersList'));
const OEMPartnerDetail = lazy(() => import('../pages/partnerships/OEMPartnerDetail'));
const CreateOEMPartner = lazy(() => import('../pages/partnerships/CreateOEMPartner'));
const CreateProposal = lazy(() => import('../pages/partnerships/CreateProposal'));
const ProposalsList = lazy(() => import('../pages/partnerships/ProposalsList'));
const InstallationDashboard = lazy(() => import('../pages/installation/InstallationDashboard'));
const InstallationProjectDetail = lazy(() => import('../pages/installation/ProjectDetail'));
const InstallationCreateSite = lazy(() => import('../pages/installation/CreateSite'));
const InstallationCreateProject = lazy(() => import('../pages/installation/CreateProject'));
const InstallationAnalytics = lazy(() => import('../pages/installation/AnalyticsDashboard'));
const FieldApp = lazy(() => import('../pages/field/FieldApp'));
const UrgaaDashboard = lazy(() => import('../pages/urgaa/UrgaaDashboard'));
const ApplicationsList = lazy(() => import('../pages/urgaa/applications/ApplicationsList'));
const ComplianceChecker = lazy(() => import('../pages/urgaa/compliance/ComplianceChecker'));
const SiteScoring = lazy(() => import('../pages/urgaa/sites/SiteScoring'));
const WorkflowDashboard = lazy(() => import('../pages/urgaa/workflows/WorkflowDashboard'));
const ChargerFleet = lazy(() => import('../pages/urgaa/assets/ChargerFleet'));
const SubsidyChecker = lazy(() => import('../pages/urgaa/subsidies/SubsidyChecker'));
const AnalyticsDashboard = lazy(() => import('../pages/urgaa/analytics/AnalyticsDashboard'));
const HomeCharging = lazy(() => import('../pages/urgaa/residential/HomeCharging'));
const FleetOptimizer = lazy(() => import('../pages/urgaa/fleet/FleetOptimizer'));
const TCOCalculator = lazy(() => import('../pages/urgaa/tco-calculator/TCOCalculator'));
const PolicyAlerts = lazy(() => import('../pages/urgaa/policy-alerts/PolicyAlerts'));
const BatteryHealth = lazy(() => import('../pages/urgaa/battery-health/BatteryHealth'));
const EVFinancing = lazy(() => import('../pages/urgaa/ev-financing/EVFinancing'));
const EVInsurance = lazy(() => import('../pages/urgaa/ev-insurance/EVInsurance'));
const GridMonitoring = lazy(() => import('../pages/urgaa/grid-monitoring/GridMonitoring'));
const RoadsideAssistance = lazy(() => import('../pages/urgaa/roadside-assistance/RoadsideAssistance'));
const AdvancedAnalyticsPage = lazy(() => import('../pages/urgaa/analytics/AdvancedAnalytics'));

export const protectedRoutes = [
  { path: '/', element: <RoleBasedDashboard /> },
  { path: '/planning/dsat', element: <DsatTargets /> },
  { path: '/planning/governance', element: <Governance /> },
  { path: '/planning/sla-credits', element: <SLACredits /> },
  { path: '/network', element: <NetworkOps /> },
  { path: '/revenue', element: <Revenue /> },
  { path: '/programs', element: <Programs /> },
  { path: '/roaming', element: <RoamingHub /> },
  { path: '/compliance', element: <Compliance /> },
  { path: '/incidents', element: <IncidentCenter /> },
  { path: '/esg', element: <ESGDashboard /> },
  { path: '/pilot', element: <PilotAnalytics /> },
  { path: '/exports', element: <ExportsCenter /> },
  { path: '/gov/planner', element: <GovPlanner /> },
  { path: '/gov/discom', element: <DISCOMBoard /> },
  { path: '/gov/snapshot', element: <StatusSnapshot /> },
  { path: '/gov/subsidy', element: <SubsidyVerifier /> },
  { path: '/gov/opendata', element: <OpenDataFeeds /> },
  { path: '/gov/policies', element: <PoliciesDocs /> },
  { path: '/workbook', element: <Workbook /> },
  { path: '/workbook/feature/:key', element: <WorkbookFeature /> },
  { path: '/admin/users', element: <UserManagement /> },
  { path: '/admin/permissions', element: <PermissionsMatrix /> },
  { path: '/admin/audit', element: <AuditTrail /> },
  { path: '/admin/approvals', element: <ApprovalWorkflows /> },
  { path: '/admin', element: <Admin /> },
  { path: '/lifecycle', element: <Lifecycle /> },
  { path: '/ops/dashboard', element: <OperationsDashboard /> },
  { path: '/finance/dashboard', element: <FinanceDashboard /> },
  { path: '/analytics', element: <AdvancedAnalytics /> },
  { path: '/fleet/dashboard', element: <FleetDashboard /> },
  { path: '/network/health-map', element: <NetworkHealthMap /> },
  { path: '/pricing', element: <PricingMatrix /> },
  { path: '/pricing/:skuId', element: <PricingDetail /> },
  { path: '/admin/reliability', element: <ReliabilityConsole /> },
  { path: '/fleet-apis', element: <FleetAPIs /> },
  { path: '/host', element: <HostPortal /> },
  { path: '/finance/einvoicing', element: <EInvoicing /> },
  { path: '/showcase', element: <ComponentShowcase /> },
  { path: '/examples/dashboard', element: <AdvancedDashboardExample /> },
  { path: '/examples/datatable', element: <DataTableDemo /> },
  { path: '/examples/kanban', element: <KanbanDemo /> },
  { path: '/network/stations-enhanced', element: <StationsManagement /> },
  { path: '/ops/incidents-kanban', element: <IncidentsKanban /> },
  { path: '/finance/credit-scorecard', element: <CreditScorecard /> },
  { path: '/govt/subsidy-calculator', element: <SubsidyCalculator /> },
  { path: '/ops/predictive-alerts', element: <PredictiveAlertsDashboard /> },
  { path: '/network/demand-heatmap', element: <DemandHeatmap /> },
  { path: '/ai', element: <AIFeatures /> },
  { path: '/ai/*', element: <AIFeatures /> },
  { path: '/partnerships', element: <PartnershipDashboard /> },
  { path: '/partnerships/dashboard', element: <PartnershipDashboard /> },
  { path: '/partnerships/oem-partners', element: <OEMPartnersList /> },
  { path: '/partnerships/oem-partners/new', element: <CreateOEMPartner /> },
  { path: '/partnerships/oem-partners/:partnerId', element: <OEMPartnerDetail /> },
  { path: '/partnerships/proposals', element: <ProposalsList /> },
  { path: '/partnerships/proposals/new', element: <CreateProposal /> },
  { path: '/installation', element: <InstallationDashboard /> },
  { path: '/installation/dashboard', element: <InstallationDashboard /> },
  { path: '/installation/analytics', element: <InstallationAnalytics /> },
  { path: '/installation/sites/new', element: <InstallationCreateSite /> },
  { path: '/installation/projects/new', element: <InstallationCreateProject /> },
  { path: '/installation/projects/:projectId', element: <InstallationProjectDetail /> },
  { path: '/field', element: <FieldApp /> },
  { path: '/urgaa', element: <UrgaaDashboard /> },
  { path: '/urgaa/applications', element: <ApplicationsList /> },
  { path: '/urgaa/compliance', element: <ComplianceChecker /> },
  { path: '/urgaa/sites', element: <SiteScoring /> },
  { path: '/urgaa/workflows', element: <WorkflowDashboard /> },
  { path: '/urgaa/assets', element: <ChargerFleet /> },
  { path: '/urgaa/subsidies', element: <SubsidyChecker /> },
  { path: '/urgaa/analytics', element: <AnalyticsDashboard /> },
  { path: '/urgaa/residential', element: <HomeCharging /> },
  { path: '/urgaa/fleet', element: <FleetOptimizer /> },
  { path: '/urgaa/tco-calculator', element: <TCOCalculator /> },
  { path: '/urgaa/policy-alerts', element: <PolicyAlerts /> },
  { path: '/urgaa/battery-health', element: <BatteryHealth /> },
  { path: '/urgaa/ev-financing', element: <EVFinancing /> },
  { path: '/urgaa/ev-insurance', element: <EVInsurance /> },
  { path: '/urgaa/grid-monitoring', element: <GridMonitoring /> },
  { path: '/urgaa/roadside-assistance', element: <RoadsideAssistance /> },
  { path: '/urgaa/advanced-analytics', element: <AdvancedAnalyticsPage /> },
];
